import React from "react";
import {
  Route,
  BrowserRouter,
  Routes,
  Outlet,
  useLocation,
} from "react-router-dom";
import NavBar from "@modules/NavBar/NavBar";
import Login from "@modules/Login";
import AuthGuard from "@components/AuthGuard";
import KickOff from "@modules/KickOff/KickOffView/KickOffView";
import KickOffList from "@modules/KickOff/KickOffList/KickOffList";
import SiteList from "@modules/Site/SiteList";
import LeadList from "@modules/Lead/LeadList";
import MeetingReportsList from "@modules/MeetingReports/MeetingReportsList/MeetingReportsList";
import CompanyView from "@modules/Company/CompanyView";
import ContactList from "@modules/Contact/ContactList";
import ProductsList from "@modules/Products/ProductsList";
import CenterProductView from "@modules/CenterProducts/CenterProductView";
import AddEditCenterProduct from "@modules/CenterProducts/AddEditCenterProduct";
import CompanyList from "@modules/Company/CompanyList";
import WarehouseView from "@modules/Warehouse/WarehouseView";
import AddEditCompany from "@modules/Company/AddEditCompany";
import SiteView from "@modules/Site/SiteView";
import AddEditSite from "@modules/Site/AddEditSite";
import EnquiryList from "@modules/Enquiry/EnquiryList";
import QuoteList from "@modules/Quote/QuoteList";
import GeneralMap from "@modules/General/GeneralMap";
import "./route.scss";
import AdminView from "@modules/Admin/AdminView/AdminView";
import Planning from "@modules/Planning/Planning";
import SupplierList from "@modules/SupplierPlanning/SupplierList";
import Dashboard from "@modules/Dashboard/DashboardView";
import LeadView from "@modules/Lead/LeadView/LeadView";
import InvoiceList from "@modules/Invoice/InvoiceList";
import AddEditInvoice from "@modules/Invoice/AddEditInvoice";
import InvoiceView from "@modules/Invoice/InvoiceView";
import AddEditMailTemplate from "@modules/MailToken/AddEditMailTemplate/AddEditMailTemplate";
import ResetPasswordModal from "@components/ResetPasswordModal/ResetPasswordModal";
import NotFound from "@components/Errors/NotFound";
import ErrorBoundary from "@components/Errors/ErrorBoundary";
import ReportingView from "@modules/Reporting/ReportingView";

// TEMPORARY: used to manually verify the 500 error page. Remove after testing.
const TestError = () => {
  throw new Error("Test 500 error");
};


// Protected layout wrapper - checks JWT auth for all child routes.
// Rendered once via a parent layout route so NavBar persists across
// client-side navigations instead of unmounting/remounting per route.
// The ErrorBoundary here only wraps the page content (Outlet), so if a
// page crashes, the sidebar/NavBar stays visible instead of being replaced
// by the OOPS fallback along with the rest of the app. It's keyed by the
// current path so it remounts (resetting its error state) on navigation,
// otherwise it would keep showing the OOPS fallback even after the user
// navigates to a different, working page.
const ProtectedLayout = () => {
  const location = useLocation();
  return (
    <AuthGuard>
      <div className="sidebar-content">
        <NavBar />
        <ErrorBoundary key={location.pathname} showHomeButton={false}>
          <Outlet />
        </ErrorBoundary>
        <ResetPasswordModal />
      </div>
    </AuthGuard>
  );
};

const RoutesConfig = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public route - no auth required */}
        <Route exact path="/login" element={<Login />} />

        {/* Protected routes - JWT auth required */}
        <Route element={<ProtectedLayout />}>
          <Route exact path="/lead/view/:id" element={<LeadView />} />
          <Route
            exact
            path="/lead/list"
            element={
              <LeadList
                columnList={[
                  "ID",
                  "LEAD_DATE",
                  "LEAD_CONTACT",
                  "LEAD_SITE",
                  "LEAD_STATUS",
                  "ACTIONS",
                ]}
              />
            }
          />
          <Route
            exact
            path="/meeting_report"
            element={
              <MeetingReportsList
                isHeader="main"
                objectType=""
                objectId=""
                columnList={[
                  "ID",
                  "TITLE",
                  "HESUS_PARTICIPANTS",
                  "MEETING_DATE",
                  "ENTITES_LINKED",
                  "RATING",
                  "MEETING_NEXT_DATE",
                  "ACTIONS",
                ]}
              />
            }
          />
          <Route
            exact
            path="/site/list"
            element={
              <SiteList
                objectType=""
                objectId=""
                columnList={[
                  "ID",
                  "COMPANY",
                  "SITE_NAME",
                  "SITE_ADDRESS",
                  "STATUS",
                ]}
              />
            }
          />
          <Route exact path="/site/view/:id/:path" element={<SiteView />} />

          <Route
            exact
            path="/site/edit/:id"
            element={<AddEditSite isEditMode />}
          />

          <Route exact path="/site/create" element={<AddEditSite />} />

          <Route
            exact
            path=":companyId/site/create"
            element={<AddEditSite />}
          />

          <Route exact path="/planning/:path" element={<Planning />} />

          <Route
            exact
            path="/planning/:clientOrderId/suppliers"
            element={<SupplierList />}
          />

          <Route exact path="/invoice/:path" element={<InvoiceList />} />

          <Route exact path="/" element={<Dashboard />} />

          <Route
            exact
            path="/company/list"
            element={
              <CompanyList
                columnList={[
                  "ID",
                  "COMPANY_NAME",
                  "COMPANY_TYPE",
                  "COMPANY_IDENTICATION_NUMBER",
                  "COMPANY_ADDRESS",
                ]}
              />
            }
          />
          <Route
            exact
            path="/contact/list"
            element={
              <ContactList
                page="main"
                dependancies={JSON.stringify(["roles", "linkedEntities"])}
                columnList={[
                  "ID",
                  "CONTACT_COMPANY",
                  "CONTACT_NAME",
                  "CONTACT_FUNCTION",
                  "CONTACT_TYPE",
                  "CONTACT_COMMUNICATION",
                  "ACTIONS",
                ]}
              />
            }
          />
          <Route
            exact
            path="/request/list/:path"
            element={
              <EnquiryList
                page="list"
                list="process"
                columnList={[
                  "ID",
                  "SITE_COMPANY",
                  "PRODUCT",
                  "OPERATION",
                  "QUANTITY",
                  "ENQUIRY_CREATION_DATE",
                  "ENQUIRY_PRIORITY",
                  "ENQUIRY_DUE_DATE",
                ]}
              />
            }
          />
          <Route
            exact
            path="/quote/list"
            element={
              <QuoteList
                page="list"
                columnList={[
                  "QUOTE",
                  "SITE_COMPANY",
                  "QUOTE_SALESMAN",
                  "QUOTE_STATUS",
                  "QUOTE_TOTAL",
                  "QUOTE_VALIDATION_DATE",
                  "QUOTE_DUNNING_INFO",
                  "ACTIONS",
                ]}
              />
            }
          />
          <Route exact path="/general/map" element={<GeneralMap />} />
          <Route exact path="/kickoff/list" element={<KickOffList />} />
          <Route
            exact
            path="/administration/emails/view/:id"
            element={<AddEditMailTemplate />}
          />

          <Route exact path="/administration/:path" element={<AdminView />} />

          {/* <Route
            exact
            path="/product/list"
            element={
              <ProductsList
                columnList={[
                  "ID",
                  "COUNTRY",
                  "CATEGORY",
                  "SUB_CATEGORY",
                  "NAME",
                  "PRODUCT_CODE",
                  "OPERATIONS",
                  "ACTIONS",
                ]}
              />
            }
          /> */}

          <Route path="/kickoff/view/:id" element={<KickOff />} />
          <Route exact path="/company/create" element={<AddEditCompany />} />
          <Route
            exact
            path="/company/edit/:id"
            element={<AddEditCompany isEditMode />}
          />
          <Route
            exact
            path="/company/view/:id/:path"
            element={<CompanyView />}
          />
          <Route
            exact
            path="/company/view/:id/product/view/:productId"
            element={<CenterProductView />}
          />
          <Route
            exact
            path="/company/view/:id/center/:centerId/product/view/:productId"
            element={<CenterProductView />}
          />
          <Route
            exact
            path="/company/view/:id/warehouse/view/:warehouseId"
            element={<WarehouseView />}
          />
          {/* edit */}
          <Route
            exact
            path="/company/view/:id/center/:centerId/product/:path/:productId"
            element={<AddEditCenterProduct isEditMode />}
          />
          {/* clone */}
          <Route
            exact
            path="/company/view/:id/center/:centerId/product/:path/:productId/:type"
            element={<AddEditCenterProduct />}
          />
          {/* create */}
          <Route
            exact
            path="/company/view/:id/center/:centerId/product/:path"
            element={<AddEditCenterProduct />}
          />
          <Route exact path="/invoice/create" element={<AddEditInvoice />} />
          <Route
            exact
            path="/invoice/edit/:id"
            element={<AddEditInvoice isEditMode />}
          />
          <Route exact path="/invoice/view/:id" element={<InvoiceView />} />
          <Route exact path="/report/:path" element={<ReportingView />} />
          <Route path="/404" element={<NotFound />} />
          {/* TEMPORARY: remove after verifying 500 page */}
          <Route path="/test-500" element={<TestError />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default RoutesConfig;
